import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Badge,
  Tooltip,
  Zoom,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DiamondIcon from '@mui/icons-material/Diamond';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import './Cart.css';

const CartDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '450px',
    maxWidth: '100%',
    background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF7F2 100%)',
    borderLeft: '1px solid rgba(198, 164, 63, 0.3)',
    boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.1)',
  },
});

const CartItem = styled(motion.div)({
  display: 'flex',
  gap: '16px',
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(198, 164, 63, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: 'rgba(198, 164, 63, 0.3)',
    boxShadow: '0 5px 20px rgba(198, 164, 63, 0.1)',
    transform: 'translateY(-2px)',
  }
});

const QuantityButton = styled(IconButton)({
  backgroundColor: 'rgba(198, 164, 63, 0.1)',
  color: '#C6A43F',
  width: '28px',
  height: '28px',
  '&:hover': {
    backgroundColor: '#C6A43F',
    color: 'white',
  },
  '&:disabled': {
    opacity: 0.3,
  }
});

const Cart = () => {
  const { 
    isCartOpen, 
    setIsCartOpen, 
    cartItems, 
    removeFromCart, 
    updateQuantity,
    getCartTotal,
    getCartCount
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const handleViewCart = () => {
    setIsCartOpen(false);
    navigate('/cart');
  };

  return (
    <CartDrawer
      anchor="right"
      open={isCartOpen}
      onClose={() => setIsCartOpen(false)}
      transitionDuration={400}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          p: 3,
          background: 'linear-gradient(135deg, #0A0F1E 0%, #1A1F2E 100%)',
          color: 'white',
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge badgeContent={getCartCount()} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#C6A43F' } }}>
                <ShoppingBagIcon />
              </Badge>
              <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                Shopping Cart
              </Typography>
            </Box>
            <IconButton onClick={() => setIsCartOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Cart Items */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <AnimatePresence>
            {cartItems.length === 0 ? (
              <Fade in={true}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  p: 3
                }}>
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <ShoppingBagIcon sx={{ fontSize: 80, color: 'rgba(198, 164, 63, 0.3)', mb: 2 }} />
                  </motion.div>
                  <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', mb: 1 }}>
                    Your cart is empty
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Discover our exquisite collection
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setIsCartOpen(false);
                      navigate('/shop');
                    }}
                    sx={{
                      bgcolor: '#C6A43F',
                      color: '#0A0F1E',
                      '&:hover': { bgcolor: '#B3923A' }
                    }}
                  >
                    Shop Now
                  </Button>
                </Box>
              </Fade>
            ) : (
              cartItems.map((item, index) => (
                <Zoom in={true} key={item.id} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Box sx={{ mb: 2 }}>
                    <CartItem
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Product Image */}
                      <Avatar
                        src={item.images?.[0]}
                        variant="rounded"
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '8px',
                          border: '1px solid rgba(198, 164, 63, 0.2)'
                        }}
                      >
                        <DiamondIcon sx={{ color: '#C6A43F' }} />
                      </Avatar>

                      {/* Product Details */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#C6A43F', display: 'block', mb: 1 }}>
                          {item.metalPurity} · {item.category}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#0A0F1E' }}>
                            ₹{(item.finalPrice * item.quantity).toLocaleString()}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <QuantityButton
                              size="small"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <RemoveIcon fontSize="small" />
                            </QuantityButton>
                            
                            <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                              {item.quantity}
                            </Typography>
                            
                            <QuantityButton
                              size="small"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            >
                              <AddIcon fontSize="small" />
                            </QuantityButton>
                            
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                onClick={() => removeFromCart(item.id)}
                                sx={{ color: '#f44336', ml: 1 }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </CartItem>
                  </Box>
                </Zoom>
              ))
            )}
          </AnimatePresence>
        </Box>

        {/* Footer */}
        {cartItems.length > 0 && (
          <Fade in={true}>
            <Box sx={{
              p: 3,
              borderTop: '1px solid rgba(198, 164, 63, 0.2)',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)'
            }}>
              {/* Subtotal */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" color="text.secondary">Subtotal</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#C6A43F' }}>
                  ₹{getCartTotal().toLocaleString()}
                </Typography>
              </Box>

              {/* Shipping Info */}
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                *Shipping charges calculated at checkout
              </Typography>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleViewCart}
                    sx={{
                      borderColor: '#C6A43F',
                      color: '#C6A43F',
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#B3923A',
                        backgroundColor: 'rgba(198, 164, 63, 0.04)'
                      }
                    }}
                  >
                    View Cart
                  </Button>
                </motion.div>
                
                <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCheckout}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: '#0A0F1E',
                      color: 'white',
                      py: 1.5,
                      '&:hover': {
                        bgcolor: '#C6A43F',
                      }
                    }}
                  >
                    Checkout
                  </Button>
                </motion.div>
              </Box>

              {/* Secure Checkout Badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  🔒 Secure Checkout
                </Typography>
                <Typography variant="caption" sx={{ color: '#C6A43F' }}>
                  ⚡ 100% Hallmarked
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}
      </Box>
    </CartDrawer>
  );
};

export default Cart;